package com.schoolconnect.app.feature.reports

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Checkbox
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.datasource.network.request.AcademicReportCriteriaType
import com.schoolconnect.app.datasource.network.request.AttendanceReportCriteriaType
import com.schoolconnect.app.datasource.network.request.CombinedPerformanceCriteriaRequest
import com.schoolconnect.app.datasource.network.request.CombinedReportCriteriaType
import com.schoolconnect.app.datasource.network.request.CombinedReportMatchType
import com.schoolconnect.app.datasource.network.request.ReportPercentageOperator
import com.schoolconnect.app.datasource.network.request.StaffReportViewBy
import com.schoolconnect.app.designsystem.AppBackground
import com.schoolconnect.app.designsystem.AppBorder
import com.schoolconnect.app.designsystem.AppButton
import com.schoolconnect.app.designsystem.AppCard
import com.schoolconnect.app.designsystem.AppDanger
import com.schoolconnect.app.designsystem.AppDialogDateField
import com.schoolconnect.app.designsystem.AppDialogTextField
import com.schoolconnect.app.designsystem.AppLabeledDropdownField
import com.schoolconnect.app.designsystem.AppMuted
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.designsystem.AppOutlinedButton
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.designsystem.AppPrimarySoft
import com.schoolconnect.app.designsystem.AppSurface
import com.schoolconnect.app.designsystem.list.AdminListEmptyState
import com.schoolconnect.app.designsystem.list.AdminListSummaryCardItem
import com.schoolconnect.app.designsystem.list.AdminListSummaryCards
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.pageCountForTotal
import com.schoolconnect.app.feature.common.pageFooterText
import com.schoolconnect.app.feature.common.pageWindow
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.health_chart
import ischoolconnect.sharedui.generated.resources.stat_users
import org.jetbrains.compose.resources.painterResource

private const val ATTENDANCE = "Attendance Report"
private const val ACADEMIC = "Academic Performance Report"
private const val COMBINED = "Combined Performance Report"
private const val SCHOOL = "School Level"
private const val CLASS = "Class Level"
private const val STUDENT = "Student Level"
private const val ALL_EXAMS = "All published exams"
private const val ALL_CATEGORIES = "All"
private val reportOperators = listOf("Less than", "Greater than", "Equal to", "Between")

@Composable
fun StaffPerformanceReportsScreen(
    isTeacher: Boolean,
    schoolId: String,
    activeAcademicYearId: String,
    modifier: Modifier = Modifier
) {
    val viewModel = remember(isTeacher, schoolId, activeAcademicYearId) {
        StaffReportsViewModel(isTeacher, schoolId, activeAcademicYearId)
    }
    DisposableEffect(viewModel) {
        onDispose { viewModel.clear() }
    }
    val state by viewModel.uiState.collectAsState()

    var reportType by remember(isTeacher) { mutableStateOf(ATTENDANCE) }
    var viewBy by remember(isTeacher) { mutableStateOf(if (isTeacher) CLASS else SCHOOL) }
    var selectedClassIds by remember { mutableStateOf<Set<String>>(emptySet()) }
    var fromDate by remember { mutableStateOf("") }
    var toDate by remember { mutableStateOf("") }
    var attendanceMetric by remember { mutableStateOf("Attendance Percentage") }
    var attendanceOperator by remember { mutableStateOf("Less than") }
    var attendanceValue by remember { mutableStateOf("") }
    var attendanceSecondValue by remember { mutableStateOf("") }
    var attendanceStatusLabel by remember { mutableStateOf("") }
    var academicMetric by remember { mutableStateOf("Overall Summary") }
    var academicOperator by remember { mutableStateOf("Less than") }
    var academicValue by remember { mutableStateOf("") }
    var academicSecondValue by remember { mutableStateOf("") }
    var academicCategory by remember { mutableStateOf(ALL_CATEGORIES) }
    var selectedExamId by remember { mutableStateOf("") }
    var combinedAttendanceEnabled by remember { mutableStateOf(false) }
    var combinedAttendanceOperator by remember { mutableStateOf("Less than") }
    var combinedAttendanceValue by remember { mutableStateOf("") }
    var combinedAttendanceSecondValue by remember { mutableStateOf("") }
    var combinedExamCategory by remember { mutableStateOf(ALL_CATEGORIES) }
    var combinedMatch by remember { mutableStateOf("All criteria") }
    var studentSearch by remember { mutableStateOf("") }
    var studentPageIndex by remember { mutableStateOf(0) }
    var selectedStudentId by remember { mutableStateOf("") }
    var errorMessage by remember { mutableStateOf("") }
    var appliedQuery by remember { mutableStateOf<StaffReportsQuery?>(null) }

    LaunchedEffect(viewModel) {
        viewModel.loadLookups()
    }
    LaunchedEffect(state.classes) {
        val validIds = state.classes.map { it.id }.toSet()
        if (selectedClassIds.isEmpty() || !validIds.containsAll(selectedClassIds)) {
            selectedClassIds = validIds
        }
    }
    LaunchedEffect(reportType, selectedClassIds) {
        if (reportType == ACADEMIC) {
            viewModel.loadExams(selectedClassIds.toList())
        }
    }
    LaunchedEffect(reportType, viewBy, selectedClassIds, studentSearch, studentPageIndex) {
        if (reportType == COMBINED && viewBy == STUDENT && selectedClassIds.size == 1) {
            viewModel.loadStudents(
                classSectionId = selectedClassIds.first(),
                query = studentSearch,
                pageIndex = studentPageIndex
            )
        }
    }
    LaunchedEffect(state.exams) {
        if (selectedExamId.isNotBlank() && state.exams.none { it.id == selectedExamId }) {
            selectedExamId = ""
        }
    }
    LaunchedEffect(state.students) {
        if (selectedStudentId.isNotBlank() && state.students.none { it.id == selectedStudentId }) {
            selectedStudentId = ""
        }
    }

    Column(
        modifier = modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .background(AppBackground)
            .padding(bottom = 24.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        ReportConfigurationCard(
            reportType = reportType,
            viewBy = viewBy,
            classes = state.classes,
            selectedClassIds = selectedClassIds,
            fromDate = fromDate,
            toDate = toDate,
            attendanceMetric = attendanceMetric,
            attendanceOperator = attendanceOperator,
            attendanceValue = attendanceValue,
            attendanceSecondValue = attendanceSecondValue,
            attendanceStatuses = state.attendanceStatuses,
            attendanceStatusLabel = attendanceStatusLabel,
            academicMetric = academicMetric,
            academicOperator = academicOperator,
            academicValue = academicValue,
            academicSecondValue = academicSecondValue,
            academicCategory = academicCategory,
            categories = state.categories.mapNotNull { it.categoryName },
            exams = state.exams,
            isLoadingExams = state.isLoadingExams,
            selectedExamId = selectedExamId,
            combinedAttendanceEnabled = combinedAttendanceEnabled,
            combinedAttendanceOperator = combinedAttendanceOperator,
            combinedAttendanceValue = combinedAttendanceValue,
            combinedAttendanceSecondValue = combinedAttendanceSecondValue,
            combinedExamCategory = combinedExamCategory,
            combinedMatch = combinedMatch,
            studentSearch = studentSearch,
            students = state.students,
            studentPageIndex = studentPageIndex,
            studentTotalCount = state.studentTotalCount,
            isLoadingStudents = state.isLoadingStudents,
            selectedStudentId = selectedStudentId,
            state = state,
            errorMessage = errorMessage,
            onReportType = {
                reportType = it
                viewBy = if (isTeacher) CLASS else SCHOOL
                academicCategory = ALL_CATEGORIES
                selectedExamId = ""
                selectedStudentId = ""
                errorMessage = ""
                appliedQuery = null
                viewModel.clearReport()
            },
            onViewBy = {
                viewBy = it
                if (it == STUDENT && selectedClassIds.size > 1) {
                    selectedClassIds = setOf(selectedClassIds.sorted().first())
                }
                selectedStudentId = ""
                studentPageIndex = 0
                errorMessage = ""
            },
            onClasses = {
                selectedClassIds = it
                selectedStudentId = ""
                studentPageIndex = 0
            },
            onFromDate = { fromDate = it; errorMessage = "" },
            onToDate = { toDate = it; errorMessage = "" },
            onAttendanceMetric = { attendanceMetric = it; errorMessage = "" },
            onAttendanceOperator = { attendanceOperator = it },
            onAttendanceValue = { attendanceValue = it.filter(Char::isDigit).take(3) },
            onAttendanceSecondValue = { attendanceSecondValue = it.filter(Char::isDigit).take(3) },
            onAttendanceStatus = { attendanceStatusLabel = it },
            onAcademicMetric = { academicMetric = it; errorMessage = "" },
            onAcademicOperator = { academicOperator = it },
            onAcademicValue = { academicValue = it.filter(Char::isDigit).take(3) },
            onAcademicSecondValue = { academicSecondValue = it.filter(Char::isDigit).take(3) },
            onAcademicCategory = { academicCategory = it },
            onExam = { selectedExamId = it },
            onCombinedAttendanceEnabled = { combinedAttendanceEnabled = it },
            onCombinedAttendanceOperator = { combinedAttendanceOperator = it },
            onCombinedAttendanceValue = { combinedAttendanceValue = it.filter(Char::isDigit).take(3) },
            onCombinedAttendanceSecondValue = { combinedAttendanceSecondValue = it.filter(Char::isDigit).take(3) },
            onCombinedExamCategory = { combinedExamCategory = it },
            onCombinedMatch = { combinedMatch = it },
            onStudentSearch = {
                studentSearch = it
                studentPageIndex = 0
                selectedStudentId = ""
            },
            onStudentPage = { studentPageIndex = it },
            onStudent = { selectedStudentId = it },
            onReset = {
                reportType = ATTENDANCE
                viewBy = if (isTeacher) CLASS else SCHOOL
                selectedClassIds = state.classes.map { it.id }.toSet()
                fromDate = ""
                toDate = ""
                attendanceMetric = "Attendance Percentage"
                attendanceOperator = "Less than"
                attendanceValue = ""
                attendanceSecondValue = ""
                attendanceStatusLabel = ""
                academicMetric = "Overall Summary"
                academicOperator = "Less than"
                academicValue = ""
                academicSecondValue = ""
                academicCategory = ALL_CATEGORIES
                selectedExamId = ""
                combinedAttendanceEnabled = false
                combinedAttendanceOperator = "Less than"
                combinedAttendanceValue = ""
                combinedAttendanceSecondValue = ""
                combinedExamCategory = ALL_CATEGORIES
                combinedMatch = "All criteria"
                studentSearch = ""
                studentPageIndex = 0
                selectedStudentId = ""
                errorMessage = ""
                appliedQuery = null
                viewModel.clearReport()
            },
            onRun = {
                val queryResult = buildStaffReportQuery(
                    reportType = reportType,
                    viewBy = viewBy,
                    classIds = selectedClassIds.toList(),
                    fromDate = fromDate,
                    toDate = toDate,
                    attendanceMetric = attendanceMetric,
                    attendanceOperator = attendanceOperator,
                    attendanceValue = attendanceValue,
                    attendanceSecondValue = attendanceSecondValue,
                    attendanceStatusLabel = attendanceStatusLabel,
                    attendanceStatuses = state.attendanceStatuses,
                    academicMetric = academicMetric,
                    academicOperator = academicOperator,
                    academicValue = academicValue,
                    academicSecondValue = academicSecondValue,
                    academicCategory = academicCategory,
                    selectedExamId = selectedExamId,
                    combinedAttendanceEnabled = combinedAttendanceEnabled,
                    combinedAttendanceOperator = combinedAttendanceOperator,
                    combinedAttendanceValue = combinedAttendanceValue,
                    combinedAttendanceSecondValue = combinedAttendanceSecondValue,
                    combinedExamCategory = combinedExamCategory,
                    combinedMatch = combinedMatch,
                    categories = state.categories,
                    selectedStudentId = selectedStudentId
                )
                queryResult.fold(
                    onSuccess = { query ->
                        errorMessage = ""
                        appliedQuery = query
                        viewModel.runReport(query)
                    },
                    onFailure = { errorMessage = it.message ?: "Check the report inputs and try again." }
                )
            }
        )

        if (state.report != null || state.isLoadingReport || state.reportError.isNotBlank()) {
            ReportResultCard(
                state = state,
                appliedQuery = appliedQuery,
                onExport = { format ->
                    appliedQuery?.let { viewModel.exportReport(it, format) }
                }
            )
        }
    }
}

@Composable
private fun ReportConfigurationCard(
    reportType: String,
    viewBy: String,
    classes: List<com.schoolconnect.app.repository.reports.StaffReportClassOption>,
    selectedClassIds: Set<String>,
    fromDate: String,
    toDate: String,
    attendanceMetric: String,
    attendanceOperator: String,
    attendanceValue: String,
    attendanceSecondValue: String,
    attendanceStatuses: List<com.schoolconnect.app.repository.reports.StaffReportAttendanceStatusOption>,
    attendanceStatusLabel: String,
    academicMetric: String,
    academicOperator: String,
    academicValue: String,
    academicSecondValue: String,
    academicCategory: String,
    categories: List<String>,
    exams: List<com.schoolconnect.app.repository.reports.StaffReportExamOption>,
    isLoadingExams: Boolean,
    selectedExamId: String,
    combinedAttendanceEnabled: Boolean,
    combinedAttendanceOperator: String,
    combinedAttendanceValue: String,
    combinedAttendanceSecondValue: String,
    combinedExamCategory: String,
    combinedMatch: String,
    studentSearch: String,
    students: List<com.schoolconnect.app.repository.reports.StaffReportStudentOption>,
    studentPageIndex: Int,
    studentTotalCount: Int,
    isLoadingStudents: Boolean,
    selectedStudentId: String,
    state: StaffReportsUiState,
    errorMessage: String,
    onReportType: (String) -> Unit,
    onViewBy: (String) -> Unit,
    onClasses: (Set<String>) -> Unit,
    onFromDate: (String) -> Unit,
    onToDate: (String) -> Unit,
    onAttendanceMetric: (String) -> Unit,
    onAttendanceOperator: (String) -> Unit,
    onAttendanceValue: (String) -> Unit,
    onAttendanceSecondValue: (String) -> Unit,
    onAttendanceStatus: (String) -> Unit,
    onAcademicMetric: (String) -> Unit,
    onAcademicOperator: (String) -> Unit,
    onAcademicValue: (String) -> Unit,
    onAcademicSecondValue: (String) -> Unit,
    onAcademicCategory: (String) -> Unit,
    onExam: (String) -> Unit,
    onCombinedAttendanceEnabled: (Boolean) -> Unit,
    onCombinedAttendanceOperator: (String) -> Unit,
    onCombinedAttendanceValue: (String) -> Unit,
    onCombinedAttendanceSecondValue: (String) -> Unit,
    onCombinedExamCategory: (String) -> Unit,
    onCombinedMatch: (String) -> Unit,
    onStudentSearch: (String) -> Unit,
    onStudentPage: (Int) -> Unit,
    onStudent: (String) -> Unit,
    onReset: () -> Unit,
    onRun: () -> Unit
) {
    AppCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().padding(20.dp), verticalArrangement = Arrangement.spacedBy(14.dp)) {
            Text("Report Configuration", color = AppNavy, fontSize = 18.sp, fontWeight = FontWeight.SemiBold)
            LabeledDropdown("Report Type", reportType, listOf(ATTENDANCE, ACADEMIC, COMBINED), onReportType)
            LabeledDropdown(
                "View By",
                viewBy,
                if (reportType == COMBINED) listOf(SCHOOL, CLASS, STUDENT) else listOf(SCHOOL, CLASS),
                onViewBy
            )
            if (state.isLoadingClasses) {
                Text("Loading classes…", color = AppMuted, fontSize = 12.sp)
            } else if (classes.isNotEmpty()) {
                if (viewBy == STUDENT) {
                    LabeledDropdown(
                        "Class",
                        classes.firstOrNull { it.id in selectedClassIds }?.label.orEmpty(),
                        classes.map { it.label },
                        { label -> classes.firstOrNull { it.label == label }?.let { onClasses(setOf(it.id)) } }
                    )
                } else {
                    FieldLabel("Classes")
                    ClassMultiSelect(classes, selectedClassIds, onClasses)
                }
            }
            if (state.classError.isNotBlank()) ErrorText(state.classError)
            if (viewBy == STUDENT && reportType == COMBINED) {
                AppDialogTextField(
                    value = studentSearch,
                    onValueChange = onStudentSearch,
                    placeholder = "Search students",
                    fieldHeight = 40.dp,
                    modifier = Modifier.fillMaxWidth()
                )
                LabeledDropdown(
                    "Student",
                    students.firstOrNull { it.id == selectedStudentId }?.label.orEmpty(),
                    students.map { it.label },
                    { label -> students.firstOrNull { it.label == label }?.let { onStudent(it.id) } }
                )
                if (isLoadingStudents) Text("Loading students…", color = AppMuted, fontSize = 12.sp)
                if (state.studentError.isNotBlank()) ErrorText(state.studentError)
                if (studentTotalCount > PaginationPolicy.DEFAULT_PAGE_SIZE) {
                    val pageCount = pageCountForTotal(studentTotalCount, PaginationPolicy.DEFAULT_PAGE_SIZE)
                    val window = pageWindow(studentPageIndex, PaginationPolicy.DEFAULT_PAGE_SIZE, studentTotalCount)
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                        Text(pageFooterText(window, "students"), color = AppMuted, fontSize = 12.sp)
                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            AppOutlinedButton(
                                onClick = { onStudentPage(studentPageIndex - 1) },
                                enabled = studentPageIndex > 0
                            ) { Text("Previous") }
                            AppOutlinedButton(
                                onClick = { onStudentPage(studentPageIndex + 1) },
                                enabled = studentPageIndex + 1 < pageCount
                            ) { Text("Next") }
                        }
                    }
                }
            }
            Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                DateInput("From Date", fromDate, onFromDate, Modifier.weight(1f))
                DateInput("To Date", toDate, onToDate, Modifier.weight(1f))
            }

            when (reportType) {
                ATTENDANCE -> {
                    LabeledDropdown("Metric", attendanceMetric, listOf("Attendance Percentage", "Attendance Status"), onAttendanceMetric)
                    if (attendanceMetric == "Attendance Percentage") {
                        PercentageCriterion(attendanceOperator, attendanceValue, attendanceSecondValue, onAttendanceOperator, onAttendanceValue, onAttendanceSecondValue)
                    } else {
                        LabeledDropdown(
                            "Status",
                            attendanceStatusLabel,
                            attendanceStatuses.map { it.label },
                            onAttendanceStatus
                        )
                        if (state.attendanceStatusError.isNotBlank()) ErrorText(state.attendanceStatusError)
                    }
                }
                ACADEMIC -> {
                    LabeledDropdown("Metric", academicMetric, listOf("Overall Summary", "Exam Percentage"), onAcademicMetric)
                    LabeledDropdown(
                        "Published exam",
                        exams.firstOrNull { it.id == selectedExamId }?.label ?: ALL_EXAMS,
                        listOf(ALL_EXAMS) + exams.map { it.label },
                        { label -> onExam(exams.firstOrNull { it.label == label }?.id.orEmpty()) }
                    )
                    if (isLoadingExams) Text("Loading published exams…", color = AppMuted, fontSize = 12.sp)
                    if (state.examError.isNotBlank()) ErrorText(state.examError)
                    if (academicMetric == "Overall Summary") {
                        LabeledDropdown("Performance category", academicCategory, listOf(ALL_CATEGORIES) + categories, onAcademicCategory)
                        if (state.categoryError.isNotBlank()) ErrorText(state.categoryError)
                    } else {
                        PercentageCriterion(academicOperator, academicValue, academicSecondValue, onAcademicOperator, onAcademicValue, onAcademicSecondValue)
                    }
                }
                COMBINED -> {
                    LabeledDropdown(
                        "Attendance criterion",
                        if (combinedAttendanceEnabled) "Attendance Percentage" else ALL_CATEGORIES,
                        listOf(ALL_CATEGORIES, "Attendance Percentage")
                    ) { onCombinedAttendanceEnabled(it != ALL_CATEGORIES) }
                    if (combinedAttendanceEnabled) {
                        PercentageCriterion(
                            combinedAttendanceOperator,
                            combinedAttendanceValue,
                            combinedAttendanceSecondValue,
                            onCombinedAttendanceOperator,
                            onCombinedAttendanceValue,
                            onCombinedAttendanceSecondValue
                        )
                    }
                    LabeledDropdown(
                        "Exam performance category",
                        combinedExamCategory,
                        listOf(ALL_CATEGORIES) + categories,
                        onCombinedExamCategory
                    )
                    if (state.categoryError.isNotBlank()) ErrorText(state.categoryError)
                    if (combinedAttendanceEnabled && combinedExamCategory != ALL_CATEGORIES) {
                        LabeledDropdown("Match", combinedMatch, listOf("All criteria", "Any criteria"), onCombinedMatch)
                    }
                }
            }

            if (errorMessage.isNotBlank()) ErrorText(errorMessage)
            HorizontalDivider(color = AppBorder)
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                TextButton(onClick = onReset) { Text("Reset", color = AppPrimary) }
                Spacer(Modifier.width(8.dp))
                AppButton(onClick = onRun) { Text("Run Report", fontSize = 14.sp) }
            }
        }
    }
}

@Composable
private fun ReportResultCard(
    state: StaffReportsUiState,
    appliedQuery: StaffReportsQuery?,
    onExport: (String) -> Unit
) {
    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        if (state.isLoadingReport) {
            AppCard(Modifier.fillMaxWidth()) {
                Text("Loading report from API…", color = AppMuted, modifier = Modifier.padding(20.dp))
            }
        }
        if (state.reportError.isNotBlank()) {
            AppCard(Modifier.fillMaxWidth()) {
                Text(state.reportError, color = AppDanger, modifier = Modifier.padding(20.dp))
            }
        }
        state.report?.let { report ->
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text(report.title + " Summary", color = AppNavy, fontSize = 20.sp, fontWeight = FontWeight.SemiBold)
                Row(Modifier.fillMaxWidth()) {
                    AdminListSummaryCards(
                        items = report.summary.mapIndexed { index, item ->
                            val icon = if (index % 2 == 0) Res.drawable.stat_users else Res.drawable.health_chart
                            AdminListSummaryCardItem(
                                item.title,
                                item.value,
                                item.detail,
                                painterResource(icon),
                                AppPrimary,
                                AppPrimarySoft,
                                AppMuted
                            )
                        }
                    )
                }
            }
            AppCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().padding(18.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text("Report Preview", color = AppNavy, fontSize = 18.sp, fontWeight = FontWeight.SemiBold)
                    if (report.rows.isEmpty()) {
                        AdminListEmptyState("No matching records found.", "Adjust the dates, selected classes, or criteria and run the report again.")
                    } else {
                        ReportTable(report.headers, report.rows)
                    }
                    Text("API preview: ${report.rows.size} of ${report.totalCount} records. Full records are available through export.", color = AppMuted, fontSize = 12.sp)
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End, verticalAlignment = Alignment.CenterVertically) {
                        AppOutlinedButton(
                            onClick = { onExport("excel") },
                            enabled = !state.isExporting
                        ) { Text(if (state.isExporting) "Exporting…" else "Export to Excel") }
                        Spacer(Modifier.width(8.dp))
                        AppButton(onClick = { onExport("pdf") }, enabled = !state.isExporting) { Text("Download PDF") }
                    }
                    if (state.feedback.isNotBlank()) Text(state.feedback, color = AppMuted, fontSize = 12.sp)
                }
            }
        }
        if (appliedQuery == null && !state.isLoadingReport && state.reportError.isBlank()) {
            AppCard(Modifier.fillMaxWidth()) {
                AdminListEmptyState("Configure the report and select Run Report.", "The report is fetched from the API only after you run it.")
            }
        }
    }
}

@Composable
private fun ReportTable(headers: List<String>, rows: List<List<String>>) {
    Column(Modifier.horizontalScroll(rememberScrollState())) {
        Row(Modifier.background(AppBackground)) {
            headers.forEach { header ->
                Text(
                    header,
                    modifier = Modifier.width(150.dp).padding(horizontal = 10.dp, vertical = 12.dp),
                    color = AppNavy,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.SemiBold,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
        rows.forEach { cells ->
            HorizontalDivider(color = AppBorder)
            Row {
                headers.indices.forEach { index ->
                    Text(
                        cells.getOrElse(index) { "—" },
                        modifier = Modifier.width(150.dp).padding(horizontal = 10.dp, vertical = 12.dp),
                        color = AppNavy,
                        fontSize = 12.sp,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }
        }
    }
}

@Composable
private fun PercentageCriterion(
    operator: String,
    value: String,
    secondValue: String,
    onOperator: (String) -> Unit,
    onValue: (String) -> Unit,
    onSecondValue: (String) -> Unit
) {
    LabeledDropdown("Operator", operator, reportOperators, onOperator)
    Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
        PercentageInput(if (operator == "Between") "From Value" else "Value", value, onValue, Modifier.weight(1f))
        if (operator == "Between") PercentageInput("To Value", secondValue, onSecondValue, Modifier.weight(1f))
    }
}

@Composable
private fun PercentageInput(label: String, value: String, onChange: (String) -> Unit, modifier: Modifier) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(6.dp)) {
        FieldLabel(label)
        AppDialogTextField(
            value = value,
            onValueChange = onChange,
            placeholder = "0–100",
            fieldHeight = 40.dp
        )
    }
}

@Composable
private fun DateInput(label: String, value: String, onChange: (String) -> Unit, modifier: Modifier) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(6.dp)) {
        FieldLabel(label)
        AppDialogDateField(value, onChange, Modifier.fillMaxWidth(), placeholder = "DD MMM YYYY", fieldHeight = 40.dp)
    }
}

@Composable
private fun LabeledDropdown(
    label: String,
    value: String,
    options: List<String>,
    onSelected: (String) -> Unit
) {
    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        FieldLabel(label)
        AppLabeledDropdownField(
            label = "",
            value = value,
            options = options,
            onSelected = onSelected,
            includeAllOption = false,
            modifier = Modifier.fillMaxWidth()
        )
    }
}

@Composable
private fun FieldLabel(value: String) {
    Text(value, color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
}

@Composable
private fun ErrorText(value: String) {
    Text(value, color = AppDanger, fontSize = 12.sp)
}

@Composable
private fun ClassMultiSelect(
    classes: List<com.schoolconnect.app.repository.reports.StaffReportClassOption>,
    selection: Set<String>,
    onChange: (Set<String>) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    val selectedOptions = classes.filter { it.id in selection }
    val label = when {
        selectedOptions.size == classes.size -> "All Classes"
        selectedOptions.isEmpty() -> "Select classes"
        else -> selectedOptions.joinToString(", ") { it.label }
    }
    Box {
        Surface(
            Modifier.fillMaxWidth().height(40.dp).clickable { expanded = true },
            shape = RoundedCornerShape(6.dp),
            color = AppSurface,
            border = BorderStroke(1.dp, AppBorder)
        ) {
            Row(Modifier.fillMaxSize().padding(horizontal = 12.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.SpaceBetween) {
                Text(label, color = AppNavy, fontSize = 14.sp, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text("⌄", color = AppNavy, fontSize = 18.sp)
            }
        }
        DropdownMenu(expanded, { expanded = false }) {
            DropdownMenuItem(
                text = { Text("All Classes") },
                onClick = {
                    onChange(if (selection.size == classes.size) emptySet() else classes.map { it.id }.toSet())
                    expanded = false
                },
                leadingIcon = {
                    Checkbox(
                        checked = selection.size == classes.size,
                        onCheckedChange = { onChange(if (it) classes.map { option -> option.id }.toSet() else emptySet()) }
                    )
                }
            )
            classes.forEach { option ->
                DropdownMenuItem(
                    text = { Text(option.label) },
                    onClick = { onChange(if (option.id in selection) selection - option.id else selection + option.id) },
                    leadingIcon = {
                        Checkbox(
                            checked = option.id in selection,
                            onCheckedChange = { onChange(if (it) selection + option.id else selection - option.id) }
                        )
                    }
                )
            }
        }
    }
}

private fun buildStaffReportQuery(
    reportType: String,
    viewBy: String,
    classIds: List<String>,
    fromDate: String,
    toDate: String,
    attendanceMetric: String,
    attendanceOperator: String,
    attendanceValue: String,
    attendanceSecondValue: String,
    attendanceStatusLabel: String,
    attendanceStatuses: List<com.schoolconnect.app.repository.reports.StaffReportAttendanceStatusOption>,
    academicMetric: String,
    academicOperator: String,
    academicValue: String,
    academicSecondValue: String,
    academicCategory: String,
    selectedExamId: String,
    combinedAttendanceEnabled: Boolean,
    combinedAttendanceOperator: String,
    combinedAttendanceValue: String,
    combinedAttendanceSecondValue: String,
    combinedExamCategory: String,
    combinedMatch: String,
    categories: List<com.schoolconnect.app.datasource.network.response.PerformanceCategoryResponse>,
    selectedStudentId: String
): Result<StaffReportsQuery> {
    val normalizedClasses = classIds.map(String::trim).filter(String::isNotBlank).distinct()
    if (normalizedClasses.isEmpty()) return Result.failure(IllegalArgumentException("Select at least one active class or section."))
    val from = toReportApiDate(fromDate) ?: return Result.failure(IllegalArgumentException("Enter a valid From Date using DD MMM YYYY."))
    val to = toReportApiDate(toDate) ?: return Result.failure(IllegalArgumentException("Enter a valid To Date using DD MMM YYYY."))
    if (from > to) return Result.failure(IllegalArgumentException("From Date must be on or before To Date."))
    val view = when (viewBy) {
        SCHOOL -> StaffReportViewBy.SCHOOL
        CLASS -> StaffReportViewBy.CLASS
        STUDENT -> StaffReportViewBy.STUDENT
        else -> return Result.failure(IllegalArgumentException("Select a supported report level."))
    }
    if (view == StaffReportViewBy.STUDENT && reportType != COMBINED) {
        return Result.failure(IllegalArgumentException("Student-level reports are available for Combined Performance only."))
    }
    if (view == StaffReportViewBy.STUDENT && normalizedClasses.size != 1) {
        return Result.failure(IllegalArgumentException("Select exactly one class for a student-level report."))
    }
    if (view == StaffReportViewBy.STUDENT && selectedStudentId.isBlank()) {
        return Result.failure(IllegalArgumentException("Select a student to run the report."))
    }

    val result = StaffReportsQuery(
        type = when (reportType) {
            ATTENDANCE -> StaffReportType.ATTENDANCE
            ACADEMIC -> StaffReportType.ACADEMIC
            COMBINED -> StaffReportType.COMBINED
            else -> return Result.failure(IllegalArgumentException("Select a supported report type."))
        },
        viewBy = view,
        classIds = normalizedClasses,
        studentId = selectedStudentId.takeIf { view == StaffReportViewBy.STUDENT },
        examIds = selectedExamId.takeIf(String::isNotBlank)?.let(::listOf).orEmpty(),
        fromDate = from,
        toDate = to,
        attendanceCriteriaType = if (reportType == ATTENDANCE) {
            if (attendanceMetric == "Attendance Status") AttendanceReportCriteriaType.ATTENDANCE_STATUS
            else AttendanceReportCriteriaType.ATTENDANCE_PERCENTAGE
        } else null,
        attendanceOperator = if (reportType == ATTENDANCE && attendanceMetric == "Attendance Percentage") {
            reportOperator(attendanceOperator)
                ?: return Result.failure(IllegalArgumentException("Select a supported attendance operator."))
        } else null,
        attendanceValues = if (reportType == ATTENDANCE && attendanceMetric == "Attendance Percentage") {
            percentageValues(attendanceOperator, attendanceValue, attendanceSecondValue)
                ?: return Result.failure(IllegalArgumentException("Enter valid attendance percentages from 0 to 100."))
        } else null,
        attendanceStatus = if (reportType == ATTENDANCE && attendanceMetric == "Attendance Status") {
            attendanceStatuses.firstOrNull { it.label == attendanceStatusLabel }?.value
                ?: return Result.failure(IllegalArgumentException("Select an attendance status returned by the server."))
        } else null,
        academicCriteriaType = if (reportType == ACADEMIC) {
            if (academicMetric == "Exam Percentage") AcademicReportCriteriaType.EXAM_PERCENTAGE
            else AcademicReportCriteriaType.OVERALL_SUMMARY
        } else null,
        academicOperator = if (reportType == ACADEMIC && academicMetric == "Exam Percentage") {
            reportOperator(academicOperator)
                ?: return Result.failure(IllegalArgumentException("Select a supported academic operator."))
        } else null,
        academicValues = if (reportType == ACADEMIC && academicMetric == "Exam Percentage") {
            percentageValues(academicOperator, academicValue, academicSecondValue)
                ?: return Result.failure(IllegalArgumentException("Enter valid academic percentages from 0 to 100."))
        } else null,
        categoryFilter = if (reportType == ACADEMIC && academicMetric == "Overall Summary") {
            academicCategory.takeIf { it != ALL_CATEGORIES }
        } else null,
        combinedCriteria = if (reportType == COMBINED) {
            val criteria = mutableListOf<CombinedPerformanceCriteriaRequest>()
            if (combinedAttendanceEnabled) {
                val operator = reportOperator(combinedAttendanceOperator)
                    ?: return Result.failure(IllegalArgumentException("Select a supported attendance operator."))
                val values = percentageValues(combinedAttendanceOperator, combinedAttendanceValue, combinedAttendanceSecondValue)
                    ?: return Result.failure(IllegalArgumentException("Enter valid combined attendance percentages from 0 to 100."))
                criteria += CombinedPerformanceCriteriaRequest(
                    criteriaType = CombinedReportCriteriaType.ATTENDANCE_SUMMARY,
                    percentageOperator = operator,
                    percentageValues = values
                )
            }
            if (combinedExamCategory != ALL_CATEGORIES) {
                val category = categories.firstOrNull { it.categoryName == combinedExamCategory }
                    ?: return Result.failure(IllegalArgumentException("Select a performance category returned by the server."))
                val minimum = category.minPercentage
                    ?: return Result.failure(IllegalArgumentException("The selected performance category is missing its minimum percentage."))
                val maximum = category.maxPercentage
                    ?: return Result.failure(IllegalArgumentException("The selected performance category is missing its maximum percentage."))
                criteria += CombinedPerformanceCriteriaRequest(
                    criteriaType = CombinedReportCriteriaType.EXAM_PERFORMANCE,
                    percentageOperator = ReportPercentageOperator.BETWEEN,
                    percentageValues = listOf(minimum, maximum),
                    categoryFilter = combinedExamCategory
                )
            }
            criteria
        } else emptyList(),
        matchType = if (combinedMatch == "Any criteria") CombinedReportMatchType.ANY else CombinedReportMatchType.ALL
    )
    return Result.success(result)
}

private fun reportOperator(value: String): ReportPercentageOperator? = when (value) {
    "Less than" -> ReportPercentageOperator.LESS_THAN
    "Greater than" -> ReportPercentageOperator.GREATER_THAN
    "Equal to" -> ReportPercentageOperator.EQUAL_TO
    "Between" -> ReportPercentageOperator.BETWEEN
    else -> null
}

private fun percentageValues(operator: String, firstText: String, secondText: String): List<Double>? {
    val first = firstText.toDoubleOrNull()?.takeIf { it in 0.0..100.0 } ?: return null
    if (operator != "Between") return listOf(first)
    val second = secondText.toDoubleOrNull()?.takeIf { it in 0.0..100.0 } ?: return null
    if (second < first) return null
    return listOf(first, second)
}
